import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alerta, Botao, Cartao, Selo, SeloDeEmpresa, quantidadeComUnidade } from "@plantus/ui";
import { producao, ErroDaApi } from "@plantus/api-client";
import type { Lote, VolumeNaTela } from "@plantus/api-client";

import { RECIPIENTE_NAO_IDENTIFICADO } from "../recipientes/estadoDoRecipiente";
import { DadosDoLote } from "./DadosDoLote";
import { ROTULO_DA_SITUACAO, TOM_DA_SITUACAO } from "./situacaoDoLote";

/**
 * A ficha de UM lote — **a tela que o QR do tambor abre**.
 *
 * <h2>Ela atende DUAS rotas, e a diferença é o que o QR carrega</h2>
 *
 * <pre>
 *   /producao/materias-primas/volume/:token   ← o QR impresso, hoje
 *   /producao/materias-primas/lote/:codigo    ← o código digitado, e as
 *                                               etiquetas antigas
 * </pre>
 *
 * <p>A primeira é a que o adesivo abre desde que o token passou a ser <b>por
 * volume</b>: ela sabe qual dos vinte tambores está na mão e mostra o peso
 * <b>daquele</b> recipiente. A segunda é o caminho manual — o código do lote
 * impresso em letra grande — e responde sobre o lote, sem saber qual volume.
 *
 * <p><b>Uma tela, e não duas</b>, porque a pergunta é a mesma: <i>"o que é isto
 * e posso usar?"</i>. Duas telas divergiriam — e a que ninguém está olhando é
 * sempre a que fica para trás.
 *
 * <h2>Ela existe porque a etiqueta deixou de afirmar a situação</h2>
 *
 * <p>A faixa preta `QUARENTENA` saiu do papel em 2026-08-13, e o raciocínio
 * fecha: a etiqueta não é trocada quando o lote é liberado — ninguém recolhe
 * vinte tambores para trocar adesivo —, então uma etiqueta que afirma
 * `QUARENTENA` vira **afirmação falsa** no dia da aprovação.
 *
 * <p>O que substituiu aquele controle foi a segregação física **e este QR**. É
 * por isso que a **situação atual** é o primeiro dado desta tela, em corpo
 * grande: ela é a única coisa aqui que o papel não consegue dizer, e é a razão
 * de o QR existir. Uma tela que a escondesse embaixo de uma lista de campos
 * devolveria o problema que a faixa deixou.
 *
 * <h2>Consulta, e só</h2>
 *
 * <p>Sem imprimir, sem editar, sem aprovar. Quem chega aqui está de luva, no
 * corredor, com o tambor na frente — a pergunta é *"o que é isto e posso
 * usar?"*, não *"quero emitir mais etiquetas"*. O QR apontava para a tela de
 * **gerar etiqueta**, que responde a pergunta de outra pessoa, em outro momento.
 *
 * <p>O caminho para a etiqueta continua existindo, na lista de lotes. Ele não
 * volta para cá: um botão de imprimir a um toque de quem escaneou um tambor é
 * como uma segunda etiqueta do mesmo lote passa a circular sem ninguém pedir.
 *
 * <h2>Por código, não por identificador</h2>
 *
 * <p>A rota é `/producao/materias-primas/lote/:codigo` porque o código é o que
 * está **impresso no tambor** — e é o token do resolvedor `/r/<token>`
 * (ADR-0006). Quando o QR não lê, quem está no corredor digita o que enxerga e
 * chega no mesmo lugar. Um identificador interno na URL tornaria o conserto
 * manual impossível exatamente quando ele é necessário.
 */
export function PaginaLote() {
  const { codigo, token } = useParams<{ codigo?: string; token?: string }>();
  const navegar = useNavigate();

  const [lote, definirLote] = useState<Lote | null>(null);
  /** O recipiente, quando se chegou aqui pelo QR de uma etiqueta. */
  const [volume, definirVolume] = useState<VolumeNaTela | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    definirErro(null);

    /*
     * A mensagem do servidor é exibida quando ele a escreveu para ser lida:
     * `422` é regra de negócio — o código antigo que existe em duas empresas,
     * o token que não corresponde a volume nenhum — e a recusa diz o que
     * aconteceu e para onde ir. Qualquer outra falha vira texto nosso:
     * mensagem de erro genérica do backend não é material de tela.
     */
    const recusar = (padrao: string) => (falha: unknown) => {
      if (falha instanceof ErroDaApi && falha.status === 422) {
        definirErro(falha.message);
        return;
      }
      definirErro(padrao);
    };

    if (token !== undefined) {
      producao
        .volumePorToken(token)
        .then((resposta) => {
          definirVolume(resposta);
          definirLote(resposta.lote);
        })
        .catch(recusar("Não foi possível carregar este volume."));
      return;
    }

    if (codigo === undefined) return;
    definirVolume(null);
    producao.lotePorCodigo(codigo).then(definirLote).catch(recusar("Não foi possível carregar este lote."));
  }, [codigo, token]);

  if (erro !== null) {
    return (
      <div className="lote">
        <p className="tela__chapeu">Produção · Matérias-primas</p>
        {/* O que foi lido, ecoado: quem escaneou precisa poder conferir se o
            que o sistema recebeu é o que está impresso no adesivo. */}
        <h1 className="lote__codigo">{codigo ?? token}</h1>
        <Alerta tom="erro" titulo={token === undefined ? "Lote não encontrado" : "Volume não encontrado"}>
          {erro}
        </Alerta>
        {token !== undefined && (
          <p className="lote__agora">
            Se o QR estiver danificado, o <strong>código do lote</strong> está
            impresso em letra grande na mesma etiqueta — busque por ele na lista.
          </p>
        )}
        <Botao aparencia="texto" onClick={() => navegar("/producao/materias-primas")}>
          Ver todos os lotes
        </Botao>
      </div>
    );
  }

  if (lote === null) {
    return <p role="status">Carregando lote…</p>;
  }

  return (
    <div className="lote">
      <header className="lote__topo">
        <p className="tela__chapeu">Produção · Matérias-primas</p>

        {/* ── 1. o que é ───────────────────────────────────────────────
            Nome e empresa antes de tudo: quem escaneou precisa confirmar,
            em um olhar, que o sistema abriu o tambor que ele tem na mão.
            A empresa é a DO LOTE, resolvida no servidor — nunca a do
            seletor do cabeçalho, que aqui nem foi escolhida. */}
        <h1 className="lote__material">{lote.materiaPrima}</h1>
        <p className="lote__empresa">
          <SeloDeEmpresa empresa={lote.empresa} />
          <span className="lote__mp">{lote.codigoMateriaPrima}</span>
        </p>

        {/* ── 2. qual lote ─────────────────────────────────────────────
            Em mono e grande, do jeito que sai na etiqueta: é assim que se
            confere que a tela e o papel falam do mesmo material. */}
        <p className="lote__codigo">{lote.codigo}</p>

        {/* ── 2b. QUAL RECIPIENTE ──────────────────────────────────────
            Só quando se chegou pelo QR de uma etiqueta, e é o que aquele
            QR comprou: a tela sabe qual dos vinte tambores está na mão.

            Vem colado no código porque as duas coisas juntas são a
            identidade do objeto FÍSICO — "este tambor" —, e é isso que
            quem está no corredor confere contra o adesivo antes de olhar
            qualquer outra coisa.

            O peso é o DESTE volume, gravado. 🔴 Ele ainda nasce da divisão
            do total: a conferência por balança não existe. A frase abaixo
            do número diz isso, porque uma balança pode discordar sem que
            nada esteja errado no sistema. */}
        {volume !== null && (
          <p className="lote__volume">
            <span className="lote__volume-numero">
              Volume {volume.numero} de {volume.total}
            </span>
            <span className="lote__volume-peso">
              {quantidadeComUnidade(volume.quantidade, lote.unidade)}
            </span>
            <span className="lote__sub">
              peso deste volume · ainda não conferido em balança
            </span>
            {/* ── EM QUAL TAMBOR ──────────────────────────────────────
                Havendo recipiente, a ficha o mostra; não havendo, ela
                ESCREVE que não sabe. Espaço em branco se lê como "o dado
                existia e alguém esqueceu" — e aqui ele significa o
                contrário: o vínculo nunca foi feito, porque não há
                plaqueta no tambor para ler (ADR-0012 item 8.4).

                É o mesmo princípio do `SeloDeEmpresa` sem empresa e da
                `FaixaAmbiente` sem ambiente: ausência de dado não
                aparece numa tela; estado NOMEADO, sim. */}
            {volume.recipiente !== null && volume.recipiente !== "" ? (
              <span className="lote__recipiente">
                Recipiente <strong>{volume.recipiente}</strong>
              </span>
            ) : (
              <span className="lote__recipiente lote__recipiente--ausente">
                {RECIPIENTE_NAO_IDENTIFICADO}
              </span>
            )}
          </p>
        )}
      </header>

      {/* ── 3. a situação ATUAL ────────────────────────────────────────
          O motivo de o QR existir, e por isso o primeiro cartão. Rótulo e
          cor saem do mesmo mapa da lista de lotes — mapa duplicado é mapa
          que diverge. A frase embaixo não é enfeite: o selo diz o estado,
          e o que decide o trabalho é o que se pode FAZER com ele. */}
      <Cartao titulo="Situação atual">
        <h2 className="lote__rotulo-secao">Situação atual</h2>
        <p className="lote__situacao">
          <Selo situacao={TOM_DA_SITUACAO[lote.situacao]}>
            {ROTULO_DA_SITUACAO[lote.situacao]}
          </Selo>
        </p>
        <p className="lote__consequencia">
          {lote.situacao === "APROVADO"
            ? "Liberado pela qualidade — pode ser usado."
            : "Não pode ser usado até a liberação da qualidade."}
        </p>
        <p className="lote__agora">
          É a situação de <strong>agora</strong>, lida do sistema. A etiqueta
          colada no tambor não afirma situação nenhuma, de propósito: ela não é
          trocada quando o lote muda de estado.
        </p>
      </Cartao>

      {/* ── 4. quanto, até quando, de onde ───────────────────────────
          O `<dl>` saiu para `DadosDoLote` quando o lote ganhou abas (#36):
          a aba *Resumo* responde a mesma pergunta, e duas cópias do mesmo
          bloco divergem na primeira correção que só uma delas receber. */}
      <Cartao titulo="O lote">
        <h2 className="lote__rotulo-secao">O lote</h2>
        <DadosDoLote lote={lote} />
      </Cartao>

      {/* O caminho de volta é um só, e é para a lista. Não há ação nesta
          tela — nem imprimir, nem editar. */}
      <p className="lote__rodape">
        <Botao aparencia="texto" onClick={() => navegar("/producao/materias-primas")}>
          Ver todos os lotes
        </Botao>
      </p>
    </div>
  );
}
